import { Loader2, User } from "lucide-react";
import { useEffect, useState } from "react";
import { get } from "@/lib/api/_client";

interface ClientData {
  id: string;
  name: string;
  type?: string;
  email?: string;
}

interface ClientListWidgetProps {
  clients: ClientData[];
  onClientClick?: (clientId: string) => void;
}

export function ClientListWidget({
  clients: initialClients,
  onClientClick,
}: ClientListWidgetProps) {
  const [clients, setClients] = useState<ClientData[]>(initialClients);
  const [loading, setLoading] = useState(false);

  // If clients only have IDs (name === id), fetch full data
  useEffect(() => {
    const needsFetch = initialClients.some((c) => c.name === c.id);
    if (!needsFetch) {
      setClients(initialClients);
      return;
    }

    setLoading(true);
    const ids = initialClients.map((c) => c.id);
    get<ClientData[]>(`/internal/clients/by-ids?ids=${ids.join(",")}`)
      .then((data) => setClients(data))
      .catch(() => setClients(initialClients))
      .finally(() => setLoading(false));
  }, [initialClients]);

  if (loading) {
    return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {clients.map((client) => (
        <button
          type="button"
          key={client.id}
          className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full bg-muted/50 hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer border border-transparent hover:border-primary/20"
          onClick={() => onClientClick?.(client.id)}
        >
          <User className="h-3 w-3 flex-shrink-0" />
          <span className="font-medium">{client.name}</span>
          {client.type && <span className="text-muted-foreground">({client.type})</span>}
        </button>
      ))}
    </div>
  );
}
